import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Video, FileText, Image, Link2, GripVertical, Upload, Loader2 } from "lucide-react";
import { useUpdateEvent } from "@/hooks/useEvents";
import type { Event } from "@/types/events";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface EventMaterial {
  type: "video" | "pdf" | "image" | "link";
  title: string;
  url: string;
}

const MATERIAL_TYPES = [
  { value: "video", label: "Video", icon: Video },
  { value: "pdf", label: "PDF", icon: FileText },
  { value: "image", label: "Image", icon: Image },
  { value: "link", label: "Link", icon: Link2 },
] as const;

interface EventMaterialsManagerProps {
  event: Event;
}

export function EventMaterialsManager({ event }: EventMaterialsManagerProps) {
  const updateEvent = useUpdateEvent();
  const [materials, setMaterials] = useState<EventMaterial[]>(
    () => (event.materials as EventMaterial[] | null) ?? []
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newMaterial, setNewMaterial] = useState<EventMaterial>({
    type: "video",
    title: "",
    url: "",
  });

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleAddMaterial = () => {
    if (!newMaterial.title.trim() || !newMaterial.url.trim()) {
      toast.error("Please enter both title and URL");
      return;
    }
    const updated = [...materials, { ...newMaterial }];
    setMaterials(updated);
    setNewMaterial({ type: "video", title: "", url: "" });
    saveMaterials(updated);
  };

  const handleRemoveMaterial = (index: number) => {
    const updated = materials.filter((_, i) => i !== index);
    setMaterials(updated);
    saveMaterials(updated);
  };

  const handleUpdateMaterial = (index: number, field: keyof EventMaterial, value: string) => {
    const updated = materials.map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    );
    setMaterials(updated);
  };

  const handleBlurSave = () => {
    saveMaterials(materials);
  };

  const saveMaterials = async (data: EventMaterial[]) => {
    setSaving(true);
    try {
      await updateEvent.mutateAsync({
        id: event.id,
        updates: { materials: data } as any,
      });
    } catch {
      // toast handled in mutation
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file: File, type: "pdf" | "image") => {
    if (!file) return;

    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      toast.error("File size must be less than 20MB");
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${event.brand_id}/${event.id}/${type}-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from("event-materials")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("event-materials")
        .getPublicUrl(fileName);

      // Add the new material with uploaded URL
      const newMat: EventMaterial = {
        type,
        title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension for title
        url: publicUrl,
      };

      const updated = [...materials, newMat];
      setMaterials(updated);
      await saveMaterials(updated);
      toast.success(`${type === "pdf" ? "PDF" : "Image"} uploaded successfully`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const getIcon = (type: string) => {
    const found = MATERIAL_TYPES.find((t) => t.value === type);
    return found ? found.icon : Link2;
  };

  const canUpload = (type: string) => type === "pdf" || type === "image";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Event Materials</h3>
        <p className="text-sm text-muted-foreground">
          Add videos, PDFs, images, or links that attendees can access
        </p>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file, "pdf");
          e.target.value = "";
        }}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file, "image");
          e.target.value = "";
        }}
      />

      {/* Quick upload buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => pdfInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Upload PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => imageInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Upload Image
        </Button>
      </div>

      {/* Existing materials */}
      <div className="space-y-3">
        {materials.map((material, index) => {
          const Icon = getIcon(material.type);
          return (
            <Card key={index} className="group">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="pt-2 text-muted-foreground">
                    <GripVertical className="h-4 w-4" />
                  </div>
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-muted shrink-0">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Select
                        value={material.type}
                        onValueChange={(v) => {
                          handleUpdateMaterial(index, "type", v as EventMaterial["type"]);
                          handleBlurSave();
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MATERIAL_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={material.title}
                        onChange={(e) => handleUpdateMaterial(index, "title", e.target.value)}
                        onBlur={handleBlurSave}
                        placeholder="Title"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={material.url}
                          onChange={(e) => handleUpdateMaterial(index, "url", e.target.value)}
                          onBlur={handleBlurSave}
                          placeholder="URL"
                          className="flex-1"
                        />
                        {canUpload(material.type) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="shrink-0"
                            disabled={uploading}
                            onClick={() => {
                              if (material.type === "pdf") {
                                pdfInputRef.current?.click();
                              } else if (material.type === "image") {
                                imageInputRef.current?.click();
                              }
                            }}
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemoveMaterial(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add new material */}
      <Card className="border-dashed">
        <CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-32">
              <Label className="text-xs">Type</Label>
              <Select
                value={newMaterial.type}
                onValueChange={(v) =>
                  setNewMaterial({ ...newMaterial, type: v as EventMaterial["type"] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATERIAL_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs">Title</Label>
              <Input
                value={newMaterial.title}
                onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
                placeholder="Material title"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">URL</Label>
              <Input
                value={newMaterial.url}
                onChange={(e) => setNewMaterial({ ...newMaterial, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <Button
              type="button"
              onClick={handleAddMaterial}
              disabled={saving || !newMaterial.title || !newMaterial.url}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {materials.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No materials added yet. Upload files or add URLs above.
        </p>
      )}
    </div>
  );
}
